<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAToast } from '@Atoms';
import { API, isAbortedRequestError } from '@Providers';
import { useSessionStore } from '@Stores';
import { FetchProfileService } from '@AuthModule';

const props = defineProps<{
  rowData: any;
  settings?: any;
}>();

const { initProfile } = useSessionStore();

const { push } = useRouter();
const { TOAST_TYPES, notify } = useAToast();

const TOAST_ERROR = {
  message: 'errors.generics.unknownIssue.message',
  title: 'errors.generics.unknownIssue.title',
  type: TOAST_TYPES.ERROR,
};

const shelterIdKey = computed(() => props.settings?.render?.idKey || '_id');
const shelterNameKey = computed(() => props.settings?.render?.nameKey || 'shelter');
const shelterName = computed(() => props.rowData?.[shelterNameKey.value] || null);

const shelterId = computed(() => {
  const value = props.rowData?.[shelterIdKey.value];

  return value?.$oid || value || null;
});

function getShelterInfo() {
  return FetchProfileService()
    .then((profileData: any) => {
      initProfile(profileData);
      return profileData;
    })
    .catch((error: unknown) => {
      if (isAbortedRequestError(error)) return;
      notify(TOAST_ERROR);
    });
}

async function onLoginAs(id: string) {
  if (!id) return;

  // NOTE :: Await loginAs API, then fetch profile BEFORE navigating
  // to prevent the route guard from aborting the profile request
  await API({ endpoint: `/api/loginas/${id}` }).catch(() => {});
  const profileData = await getShelterInfo();
  const routeName = profileData?.shelter?.profile ? 'animals-in-care' : 'onboarding';

  push({ name: routeName });
}
</script>

<template>
  <Button
    v-if="shelterId && shelterName"
    v-tooltip.left="{ value: $t('shelters.tooltips.login', { shelterName }), showDelay: 1000 }"
    outlined
    class="af-button w-full text-xs"
    icon="pi pi-sign-in"
    iconPos="right"
    pt:label="text-ellipsis overflow-hidden"
    :label="shelterName"
    @click="onLoginAs(shelterId)"
  />
</template>
